import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Brain, Sparkles, ArrowRight, MessageCircle, TrendingUp, X, Info } from 'lucide-react';

// About eternitysingles content
const ABOUT_CONTENT = {
  title: "About eternitysingles®",
  tagline: "Connecting Hearts, Nurturing Minds",
  sections: [
    {
      title: "Our Mission",
      content: "We believe that true love flourishes when both partners are mentally and emotionally healthy. Our platform creates a safe, supportive environment where singles can connect based on shared values around mental wellness, personal growth, and authentic relationships."
    },
    {
      title: "What Makes Us Different",
      items: [
        { label: "Mental Health Focus", desc: "We integrate wellness tools, mood tracking, and support resources directly into the dating experience." },
        { label: "Support Groups", desc: "Connect with others who share similar experiences through our community support groups." },
        { label: "Wellness Resources", desc: "Access guided meditations, breathing exercises, journaling prompts, and self-care activities." },
        { label: "Safe Space", desc: "We prioritize creating a judgment-free environment where you can be your authentic self." },
        { label: "Meaningful Matches", desc: "Our matching considers mental health goals, therapy experience, and openness levels." }
      ]
    },
    {
      title: "Our Values",
      values: [
        { emoji: "🌱", label: "Growth", desc: "We encourage personal development and self-improvement." },
        { emoji: "💚", label: "Compassion", desc: "We foster understanding and empathy in all interactions." },
        { emoji: "🔒", label: "Privacy", desc: "Your mental health journey is personal, and we protect it." },
        { emoji: "🤝", label: "Community", desc: "We believe in the power of support and connection." }
      ]
    },
    {
      title: "Who We're For",
      content: "eternitysingles® welcomes anyone 18+ who values mental wellness and seeks genuine connections. Whether you're managing anxiety, recovering from past relationships, or simply prioritising your emotional health, you belong here."
    }
  ],
  footer: "© 2026 eternitysingles®. All rights reserved. Developer Tyrus."
};

const LandingPage = () => {
  const [activeModal, setActiveModal] = useState(null);
  const [showAboutModal, setShowAboutModal] = useState(false);

  const features = [
    {
      id: "mood-insights",
      icon: <Brain className="w-8 h-8 text-emerald-600" />,
      title: "AI-Powered Mood Insights",
      description: "Track your emotional journey with intelligent analysis and personalised wellness recommendations.",
      modalContent: {
        title: "AI-Powered Mood Insights",
        icon: <Brain className="w-12 h-12 text-emerald-600" />,
        description: "Our advanced AI analyses your mood patterns to provide personalised wellness insights and recommendations for your mental health journey.",
        features: [
          "Daily mood tracking with 10-point scale and emotional tags",
          "AI analysis of mood patterns and trends over time", 
          "Personalised wellness suggestions based on your data",
          "Integration with your dating preferences for better matches",
          "Privacy-focused data that only you can see"
        ],
        benefits: "Understanding your emotional patterns helps you make better decisions about relationships, self-care, and personal growth while connecting with others who complement your wellness journey."
      }
    },
    {
      id: "compatibility",
      icon: <Heart className="w-8 h-8 text-emerald-600" />,
      title: "Mental Health Compatibility", 
      description: "Connect with people who understand your wellness goals and therapy journey.",
      modalContent: {
        title: "Mental Health Compatibility Matching",
        icon: <Heart className="w-12 h-12 text-emerald-600" />,
        description: "Our unique matching algorithm considers mental health goals, therapy experience, and wellness preferences alongside traditional compatibility factors.",
        features: [
          "Matching based on shared mental health goals and experiences",
          "Therapy experience compatibility (current, past, or none)",
          "Communication style preferences (private, open, transparent)",
          "Relationship goals alignment (friendship, dating, support)",
          "Interest-based connections with wellness focus"
        ],
        benefits: "Find someone who truly understands your journey and supports your growth, creating deeper, more meaningful connections built on mutual understanding and shared wellness values."
      }
    },
    {
      id: "communities",
      icon: <Users className="w-8 h-8 text-emerald-600" />,
      title: "Support Communities",
      description: "Join safe spaces where you can share experiences and grow together.",
      modalContent: {
        title: "Supportive Communities",
        icon: <Users className="w-12 h-12 text-emerald-600" />,
        description: "Connect with others in moderated, safe spaces designed for sharing experiences, offering support, and growing together in your wellness journey.",
        features: [
          "Topic-focused groups (anxiety, depression, relationships, general wellness)",
          "Public and private group options for different comfort levels",
          "Moderated discussions to ensure safe, supportive environments",
          "Share experiences and learn from others' journeys",
          "Build friendships and support networks beyond romantic connections"
        ],
        benefits: "Being part of a community that understands your challenges reduces isolation, provides valuable support, and creates opportunities for both platonic and romantic connections."
      }
    },
    {
      id: "wellness",
      icon: <Sparkles className="w-8 h-8 text-emerald-600" />,
      title: "Wellness Features",
      description: "Access guided meditations, breathing exercises, and self-care activities for your wellbeing.",
      modalContent: {
        title: "Wellness Features",
        icon: <Sparkles className="w-12 h-12 text-emerald-600" />,
        description: "A comprehensive suite of wellness tools designed to support your mental health journey and help you show up as your best self in relationships.",
        features: [
          "Guided meditations for stress relief and emotional balance",
          "Breathing exercises for anxiety management",
          "Journaling prompts for self-reflection and growth",
          "Self-care activity suggestions and reminders",
          "Wellness challenges to build healthy habits"
        ],
        benefits: "Investing in your mental wellness not only improves your overall quality of life but also helps you build healthier, more fulfilling relationships with others."
      }
    }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      text: "Finally found someone who truly understands my anxiety journey. We support each other's growth every day.",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b5c5?w=150&h=150&fit=crop&crop=face"
    },
    {
      name: "David L.",
      text: "The mood tracking helped me understand my patterns, and I met my partner in a support group here.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
    },
    {
      name: "Maya K.",
      text: "This isn't just dating – it's a community that prioritises mental wellness and authentic connection.",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="glass-strong border-b border-emerald-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img 
                src="/logo.png" 
                alt="eternitysingles" 
                className="h-12 w-auto object-contain"
                style={{ maxWidth: '300px' }}
              />
            </div>
            <Link
              to="/auth"
              className="btn-primary flex items-center space-x-2"
              data-testid="get-started-btn"
            >
              <span>Get Started</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/50 via-teal-100/30 to-cyan-100/50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className="text-4xl md:text-6xl font-bold text-emerald-800 leading-tight mb-6">
                Find Love While
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-green-600">
                  Nurturing Wellness
                </span>
              </h1>
              <p className="text-xl text-emerald-700 mb-6 leading-relaxed">
                Connect with like-minded individuals who prioritise mental health, emotional growth, and authentic relationships. Your journey to wellness and love starts here.
              </p>
              <div className="bg-emerald-100 border border-emerald-300 rounded-lg p-3 mb-8 inline-block">
                <p className="text-emerald-800 font-medium text-sm">
                  Adults 18+ Only - Age-Restricted Platform
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/auth"
                  className="btn-primary text-lg px-8 py-4 flex items-center justify-center space-x-2"
                  data-testid="hero-signup-btn"
                >
                  <Sparkles className="w-5 h-5" />
                  <span>Start Your Journey</span>
                </Link>
                <button 
                  onClick={() => {
                    document.getElementById('features-section')?.scrollIntoView({ 
                      behavior: 'smooth' 
                    });
                  }}
                  className="btn-secondary text-lg px-8 py-4 flex items-center justify-center space-x-2"
                  data-testid="learn-more-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Learn More</span>
                </button>
              </div>
            </div>
            <div className="animate-fade-in-scale stagger-2">
              <div className="relative group cursor-pointer block">
                <img
                  src="https://images.unsplash.com/photo-1591969851586-adbbd4accf81?w=600&h=600&fit=crop"
                  alt="Romantic couple on beach at sunset - connection and wellness"
                  className="rounded-3xl shadow-2xl w-full max-w-lg mx-auto transition-all duration-700 group-hover:scale-105 group-hover:brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/20 to-transparent rounded-3xl transition-all duration-700 group-hover:from-pink-500/30"></div>
                
                {/* Heart button - clickable to sign up */}
                <div className="absolute inset-0 overflow-hidden rounded-3xl">
                  <Link 
                    to="/auth" 
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-500 z-10"
                    data-testid="heart-signup-btn"
                  >
                    <div className="animate-ping absolute inline-flex h-16 w-16 rounded-full bg-pink-400 opacity-75"></div>
                    <div className="relative inline-flex rounded-full h-16 w-16 bg-gradient-to-br from-pink-500 to-red-500 items-center justify-center hover:scale-110 transition-transform">
                      <svg className="w-10 h-10 text-white animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </Link>
                </div>
                
                {/* Interactive text overlay */}
                <div className="absolute bottom-6 left-0 right-0 text-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0 pointer-events-none">
                  <p className="text-white font-semibold text-lg drop-shadow-lg">
                    💕 Find Your Connection 💕
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/30 to-white/50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-emerald-800 mb-6">
              Wellness-First Dating Experience
            </h2>
            <p className="text-xl text-emerald-600 max-w-3xl mx-auto">
              Our platform combines the science of psychology with the art of human connection. It graciously offers individuals the opportunity to discover their true love or forge a friendship without any form of judgment on mental health, as it may be. We graciously welcome those who find it challenging to establish connections.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                onClick={() => setActiveModal(feature.id)}
                className={`card-gradient animate-fade-in-up stagger-${index + 1} cursor-pointer hover:scale-105 transition-transform duration-300`}
                data-testid={`feature-${feature.id}`}
              >
                <div className="mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-emerald-800 mb-4">
                  {feature.title}
                </h3>
                <p className="text-emerald-600 leading-relaxed mb-4">
                  {feature.description}
                </p>
                <div className="flex items-center text-emerald-600 font-medium text-sm">
                  <span>Learn More</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-emerald-800 mb-6">
              Your Path to Mindful Connection
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center animate-fade-in-up">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold text-emerald-800 mb-4">Create Your Wellness Profile</h3>
              <p className="text-emerald-600">Share your mental health journey, goals, and what you're looking for in a supportive partner.</p>
            </div>
            
            <div className="text-center animate-fade-in-up stagger-2">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold text-emerald-800 mb-4">Track & Reflect</h3>
              <p className="text-emerald-600">Use our mood tracker and AI insights to understand your emotional patterns and growth.</p>
            </div>
            
            <div className="text-center animate-fade-in-up stagger-3">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold text-emerald-800 mb-4">Connect Meaningfully</h3>
              <p className="text-emerald-600">Meet compatible matches based on wellness goals and join supportive community groups.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-emerald-800 mb-6">
              Stories of Connection & Growth
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className={`card animate-fade-in-scale stagger-${index + 1}`}>
                <div className="mb-4">
                  <h4 className="font-semibold text-emerald-800">{testimonial.name}</h4>
                </div>
                <p className="text-emerald-600 italic leading-relaxed">
                  "{testimonial.text}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Find Your Mindful Match?
          </h2>
          <p className="text-xl mb-8 text-emerald-100">
            Join thousands who are building meaningful relationships while prioritising mental wellness.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center space-x-3 bg-white text-emerald-700 font-semibold px-8 py-4 rounded-xl hover:bg-emerald-50 transition-all duration-300 hover:scale-105"
            data-testid="cta-signup-btn"
          >
            <Heart className="w-5 h-5" />
            <span>Start Your Journey Today</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-2">
              <img 
                src="/logo_footer.png" 
                alt="eternitysingles" 
                className="h-10 w-auto object-contain brightness-0 invert mx-auto"
                style={{ maxWidth: '180px', display: 'block', marginLeft: '-10px' }}
              />
            </div>
            <div className="text-emerald-200">
              <button
                onClick={() => setShowAboutModal(true)}
                className="hover:text-white transition-colors cursor-pointer"
                data-testid="footer-about-btn"
              >
                eternitysingles<sup>®</sup>
              </button>
              <span className="ml-2 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full uppercase tracking-wide">
                Beta
              </span>
              <br />
              Connecting hearts, nurturing minds.
              <br />
              © 2026 All rights reserved. Developer Tyrus.
            </div>
          </div>
        </div>
      </footer>

      {/* About eternitysingles Modal */}
      {showAboutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl animate-fade-in-scale">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-green-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Heart className="w-8 h-8" />
                  <div>
                    <h2 className="text-xl font-bold">{ABOUT_CONTENT.title}</h2>
                    <p className="text-emerald-100 text-sm">{ABOUT_CONTENT.tagline}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAboutModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[55vh]">
              {ABOUT_CONTENT.sections.map((section, index) => (
                <div key={index} className="mb-6">
                  <h3 className="text-lg font-semibold text-emerald-800 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                    {section.title}
                  </h3>
                  
                  {section.content && (
                    <p className="text-emerald-700 leading-relaxed">{section.content}</p>
                  )}
                  
                  {section.items && (
                    <ul className="space-y-3">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start space-x-3 p-3 bg-emerald-50 rounded-lg">
                          <span className="text-emerald-500 font-bold">•</span>
                          <div>
                            <span className="font-medium text-emerald-800">{item.label}:</span>
                            <span className="text-emerald-600 ml-1">{item.desc}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.values && (
                    <div className="grid grid-cols-2 gap-3">
                      {section.values.map((value, i) => (
                        <div key={i} className="flex items-center space-x-3 p-3 bg-emerald-50 rounded-lg">
                          <span className="text-2xl">{value.emoji}</span>
                          <div>
                            <p className="font-medium text-emerald-800">{value.label}</p>
                            <p className="text-xs text-emerald-600">{value.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 p-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs text-emerald-600">{ABOUT_CONTENT.footer}</p>
                <button
                  onClick={() => setShowAboutModal(false)}
                  className="btn-primary px-6"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Modal */}
      {activeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fade-in-scale">
            {(() => {
              const feature = features.find(f => f.id === activeModal);
              if (!feature) return null;
              
              return (
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center">
                        {feature.modalContent.icon}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-emerald-800">
                          {feature.modalContent.title}
                        </h2>
                      </div>
                    </div>
                    <button
                      onClick={() => setActiveModal(null)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      data-testid="close-modal"
                    >
                      <X className="w-6 h-6 text-gray-500" />
                    </button>
                  </div>

                  {/* Description */}
                  <p className="text-emerald-700 text-lg mb-6 leading-relaxed">
                    {feature.modalContent.description}
                  </p>

                  {/* Features List */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-emerald-800 mb-4 flex items-center">
                      <Sparkles className="w-5 h-5 mr-2" />
                      Key Features
                    </h3>
                    <div className="space-y-3">
                      {feature.modalContent.features.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-emerald-700">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-semibold text-emerald-800 mb-2 flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2" />
                      Benefits for You
                    </h3>
                    <p className="text-emerald-700 leading-relaxed">
                      {feature.modalContent.benefits}
                    </p>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/auth"
                      className="btn-primary flex-1 text-center py-3 flex items-center justify-center space-x-2"
                    >
                      <Heart className="w-5 h-5" />
                      <span>Join EternitySingles®</span>
                    </Link>
                    <button
                      onClick={() => setActiveModal(null)}
                      className="btn-secondary flex-1 py-3"
                    >
                      Continue Browsing
                    </button>
                  </div>

                  {/* No special CTA needed */}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;